import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { LayoutDashboard, Users, FileCheck2, DollarSign, ShieldAlert, BarChart, MessageSquare, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import Divider from '@/components/ui/divider';
import { adminSidebarLinks } from '@/config/adminSidebarLinks';

const mockDisputes = [
  { id: 1, missionId: '#M1982', client: 'Client Express', transporter: 'Fatoumata Camara', subject: 'Marchandise endommagée', date: '2025-04-12', status: 'open' },
  { id: 2, missionId: '#M1975', client: 'Mamadou Diallo', transporter: 'Ibrahima Sory', subject: 'Retard de livraison important', date: '2025-04-11', status: 'open' },
  { id: 3, missionId: '#M1950', client: 'Commerce SARL', transporter: 'Alpha Barry', subject: 'Paiement non reçu', date: '2025-04-07', status: 'resolved' },
];

const AdminDisputes = () => {
  const [disputes, setDisputes] = useState([]);
  const [isResolveDialogOpen, setIsResolveDialogOpen] = useState(false);
  const [disputeToResolve, setDisputeToResolve] = useState(null);
  const [resolutionMessage, setResolutionMessage] = useState('');

  useEffect(() => {
    const storedDisputes = JSON.parse(localStorage.getItem('admin_disputes'));
    if (storedDisputes) {
      setDisputes(storedDisputes);
    } else {
      setDisputes(mockDisputes);
      localStorage.setItem('admin_disputes', JSON.stringify(mockDisputes));
    }
  }, []);

  const openResolveDialog = (dispute) => {
    setDisputeToResolve(dispute);
    setIsResolveDialogOpen(true);
  };

  const confirmResolve = () => {
    if (!resolutionMessage.trim()) {
      toast({ title: 'Erreur', description: 'Veuillez entrer un message de résolution.', variant: 'destructive' });
      return;
    }
    const updatedDisputes = disputes.map(d => d.id === disputeToResolve.id ? { ...d, status: 'resolved' } : d);
    setDisputes(updatedDisputes);
    localStorage.setItem('admin_disputes', JSON.stringify(updatedDisputes));
    toast({ title: 'Litige résolu', description: `Le litige pour la mission ${disputeToResolve.missionId} a été marqué comme résolu.` });
    setIsResolveDialogOpen(false);
    setDisputeToResolve(null);
    setResolutionMessage('');
  };

  const openDisputes = disputes.filter(d => d.status === 'open');

  const getStatusBadge = (status) => {
    const statusMap = {
      open: 'bg-red-500/20 text-red-400 border-red-500/30',
      resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
    };
    return <Badge className={`capitalize ${statusMap[status]}`}>{status === 'open' ? 'Ouvert' : 'Résolu'}</Badge>;
  };
  
  return (
    <>
      <Helmet><title>Gestion des Litiges - LogiConnect</title></Helmet>
      <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <h1 className="text-3xl font-bold text-white mb-8">Gestion des Litiges</h1>

        <div className="bg-primary-700/50 border border-primary-600 rounded-xl">
          <div className="p-4 sm:p-6 border-b border-primary-600">
            <h2 className="text-xl font-semibold text-white">Litiges ouverts ({openDisputes.length})</h2>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            {openDisputes.length > 0 ? openDisputes.map((item) => (
              <div key={item.id} className="bg-primary-900/70 p-4 rounded-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:bg-primary-900 transition-colors">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-2">
                    <span className="font-semibold text-lg text-white">Mission {item.missionId}</span>
                    {getStatusBadge(item.status)}
                  </div>
                  <p className="text-primary-200">Sujet: {item.subject}</p>
                  <div className="text-sm text-primary-400 flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-1 mt-1">
                    <span>Client: {item.client}</span>
                    <span>Transporteur: {item.transporter}</span>
                    <span className="hidden lg:inline">Date: {item.date}</span>
                  </div>
                </div>
                <div className="flex gap-2 self-end md:self-center">
                  <Button variant="outline" className="border-primary-500 hover:bg-primary-700">
                    <Eye className="w-4 h-4 mr-2" />
                    Détails
                  </Button>
                  <Button onClick={() => openResolveDialog(item)} className="bg-secondary text-primary font-semibold">
                    <MessageSquare className="w-4 h-4 mr-2" /> Résoudre
                  </Button>
                </div>
              </div>
            )) : <p className="text-primary-300 text-center py-8">Aucun litige ouvert.</p>}
          </div>
        </div>
        
        <Dialog open={isResolveDialogOpen} onOpenChange={setIsResolveDialogOpen}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Résoudre le litige</DialogTitle>
                    <DialogDescription>
                        Mission {disputeToResolve?.missionId}: {disputeToResolve?.subject}
                    </DialogDescription>
                </DialogHeader>
                <div>
                  <label htmlFor="resolutionMessage" className="text-sm font-medium text-primary-200 mb-2 block">Message de résolution</label>
                  <Textarea 
                    id="resolutionMessage"
                    placeholder="Expliquez la résolution, les actions prises..."
                    className="bg-primary-900 border-primary-600 text-white"
                    value={resolutionMessage}
                    onChange={(e) => setResolutionMessage(e.target.value)}
                  />
                </div>
                <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2">
                    <DialogClose asChild>
                        <Button variant="outline" className="border-primary-500 hover:bg-primary-700">Annuler</Button>
                    </DialogClose>
                    <Button onClick={confirmResolve} className="bg-green-600 hover:bg-green-700 text-white">Marquer comme résolu</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
      </DashboardLayout>
    </>
  );
};

export default AdminDisputes;
